// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the Situation room's starting Khipus (docs/AYNI_XR_CONTROL_ROOM_PLAN.md):
 *
 * - **The board** (CR0, CR1): what a wall shows on a loop when no briefing
 *   is on it, and what "Show the board" puts on the operator's globes. Fire,
 *   smoke, wind and rain side by side on their latest frames, then the
 *   disaster alerts and earthquakes, then the next days' rain, fire danger
 *   and smoke forecasts. Timed steps, so it plays by itself.
 * - **The 72-hour briefing** (CR3): the same picture in the order a briefer
 *   tells it, moved on by the briefer, with a note for them on every step
 *   and a last step that records the decision taken.
 *
 * Both are `briefing` Khipus the room's members can change in the Khipu
 * editor (a changed board is kept on the server for the walls). Streams are
 * named by their index id (`dsaId`), the way lessons name them; every one
 * here is cleared for commercial streaming with its credit
 * (docs/AYNI_XR_STREAM_RIGHTS.json, 2026-10-08; GDACS and earthquakes
 * still say `needs_check` for paid renders, not for streaming).
 */

import { newKhipuId, type Khipu, type KhipuDatasetRef, type KhipuStep, type KhipuText, type KhipuView } from './khipu'
import { KHIPU_SCHEMA_VERSION } from './khipu'

/** The streams the templates use: index id, and a title for when the index is not loaded. */
export const SITUATION_STREAMS = {
  fire: { dsaId: '0d90ca8cac5e16e4', title: { en: 'Active Fires (FIRMS)', es: 'Incendios activos (FIRMS)', ca: 'Incendis actius (FIRMS)' } },
  smoke: { dsaId: '4402a334ffc68f68', title: { en: 'Aerosol Optical Depth', es: 'Profundidad óptica de aerosoles', ca: "Profunditat òptica d'aerosols" } },
  wind: { dsaId: 'ae106b383524c78a', title: { en: 'Wind Speed (GFS)', es: 'Velocidad del viento (GFS)', ca: 'Velocitat del vent (GFS)' } },
  rain: { dsaId: '53f18547f2d1f069', title: { en: 'Precipitation (IMERG NRT)', es: 'Precipitación (IMERG NRT)', ca: 'Precipitació (IMERG NRT)' } },
  alerts: { dsaId: '16f4feb5ad771865', title: { en: 'Disaster Alerts (GDACS)', es: 'Alertas de desastres (GDACS)', ca: 'Alertes de desastres (GDACS)' } },
  quakes: { dsaId: '45d491294d260ca4', title: { en: 'Earthquakes', es: 'Terremotos', ca: 'Terratrèmols' } },
  rainForecast: { dsaId: '685edd80aeec3b88', title: { en: 'Forecast: Precipitation (GFS)', es: 'Previsión: precipitación (GFS)', ca: 'Previsió: precipitació (GFS)' } },
  fireDanger: { dsaId: 'b358353eed105b12', title: { en: 'Forecast: Fire Danger (FWI)', es: 'Previsión: peligro de incendio (FWI)', ca: "Previsió: perill d'incendi (FWI)" } },
  smokeForecast: { dsaId: 'f18395b39946c625', title: { en: 'Forecast: Wildfire Smoke (Global)', es: 'Previsión: humo de incendios (global)', ca: "Previsió: fum d'incendis (global)" } },
} as const satisfies Record<string, { dsaId: string; title: KhipuText }>

type StreamKey = keyof typeof SITUATION_STREAMS

/** Where the templates look: Europe and the western Mediterranean. Members change it in the editor. */
export const SITUATION_DEFAULT_VIEW: KhipuView = { lat: 44, lon: 6, zoom: 2.6 }
const WORLD_VIEW: KhipuView = { lat: 20, lon: 10, zoom: 1.4 }

const datasets = (): KhipuDatasetRef[] =>
  (Object.keys(SITUATION_STREAMS) as StreamKey[]).map((key) => ({
    key,
    ref: `stream:${key}`,
    dsaId: SITUATION_STREAMS[key].dsaId,
    title: { ...SITUATION_STREAMS[key].title },
  }))

const base = (title: KhipuText, steps: KhipuStep[], now: Date): Khipu => ({
  format: 'ayni-khipu',
  schemaVersion: KHIPU_SCHEMA_VERSION,
  id: newKhipuId(),
  kind: 'briefing',
  status: 'published',
  title,
  languages: ['en', 'es', 'ca'],
  created: now.toISOString(),
  modified: now.toISOString(),
  privacy: { mode: 'anonymous' },
  datasets: datasets(),
  path: { steps },
})

/** Fire, smoke, wind and rain on four globes, rain first: the finest cadence leads the date, the others hold their own latest. */
const fourGlobes = (n: number, title: KhipuText, extra: Partial<KhipuStep> = {}): KhipuStep => ({
  id: `s${n}`,
  n,
  title,
  dataset: 'rain',
  view: SITUATION_DEFAULT_VIEW,
  time: { relative: 'latest', playing: false },
  layout: '4',
  globes: [{ dataset: 'fire' }, { dataset: 'smoke' }, { dataset: 'wind' }],
  ...extra,
})

const one = (n: number, dataset: StreamKey, title: KhipuText, opts: { forecast?: boolean; view?: KhipuView } & Partial<KhipuStep> = {}): KhipuStep => {
  const { forecast, view, ...extra } = opts
  return {
    id: `s${n}`,
    n,
    title,
    dataset,
    view: view ?? SITUATION_DEFAULT_VIEW,
    // A forecast plays its run from the start; an observation holds its newest frame.
    time: forecast ? { relative: 'first', playing: true } : { relative: 'latest', playing: false },
    layout: '1',
    ...extra,
  }
}

const timer = (seconds: number) => ({ advance: { mode: 'timer' as const, seconds } })
const note = (en: string, es: string, ca: string) => ({ kind: 'note', audience: 'presenter', text: { en, es, ca } })

/** The board a new situation room starts with. */
export function defaultBoard(now: Date = new Date()): Khipu {
  return base({ en: 'Situation board', es: 'Tablero de situación', ca: 'Tauler de situació' }, [
    fourGlobes(1, { en: 'Now: rain, fire, smoke, wind', es: 'Ahora: lluvia, fuego, humo, viento', ca: 'Ara: pluja, foc, fum, vent' }, timer(60)),
    one(2, 'alerts', { en: 'Disaster alerts', es: 'Alertas de desastres', ca: 'Alertes de desastres' }, { view: WORLD_VIEW, ...timer(30) }),
    one(3, 'quakes', { en: 'Earthquakes', es: 'Terremotos', ca: 'Terratrèmols' }, { view: WORLD_VIEW, ...timer(30) }),
    one(4, 'rainForecast', { en: 'Rain: the next days', es: 'Lluvia: los próximos días', ca: 'Pluja: els propers dies' }, { forecast: true, ...timer(45) }),
    one(5, 'fireDanger', { en: 'Fire danger: the next days', es: 'Peligro de incendio: los próximos días', ca: "Perill d'incendi: els propers dies" }, { forecast: true, ...timer(45) }),
    one(6, 'smokeForecast', { en: 'Smoke: the next days', es: 'Humo: los próximos días', ca: 'Fum: els propers dies' }, { forecast: true, ...timer(45) }),
  ], now)
}

/** The kind of prompt a Decision step carries: the player asks the briefer what was decided. */
export const DECISION_PROMPT = 'decision'

/** The 72-hour briefing a member starts from. */
export function briefingTemplate(now: Date = new Date()): Khipu {
  return base({ en: '72-hour briefing', es: 'Informe de 72 horas', ca: 'Informe de 72 hores' }, [
    fourGlobes(1, { en: 'The situation now', es: 'La situación ahora', ca: 'La situació ara' }, {
      prompts: [note('Start with what is happening now. Each globe says at the top how old its data is.', 'Empieza por lo que pasa ahora. Cada globo indica arriba la antigüedad de sus datos.', "Comença pel que passa ara. Cada globus indica a dalt l'antiguitat de les seves dades.")],
    }),
    one(2, 'alerts', { en: 'Alerts in force', es: 'Alertas en vigor', ca: 'Alertes en vigor' }, {
      view: WORLD_VIEW,
      prompts: [note('Name the alerts that touch our area and their level.', 'Nombra las alertas que afectan a nuestra zona y su nivel.', 'Anomena les alertes que afecten la nostra zona i el seu nivell.')],
    }),
    one(3, 'rainForecast', { en: 'Rain: the next days', es: 'Lluvia: los próximos días', ca: 'Pluja: els propers dies' }, {
      forecast: true,
      prompts: [note('Where and when the heaviest rain falls. Pause on the worst moment.', 'Dónde y cuándo cae la lluvia más intensa. Pausa en el peor momento.', 'On i quan cau la pluja més intensa. Atura en el pitjor moment.')],
    }),
    one(4, 'fireDanger', { en: 'Fire danger: the next days', es: 'Peligro de incendio: los próximos días', ca: "Perill d'incendi: els propers dies" }, {
      forecast: true,
      prompts: [note('Which days the danger peaks, and where.', 'Qué días alcanza el peligro su máximo, y dónde.', 'Quins dies el perill arriba al màxim, i on.')],
    }),
    one(5, 'smokeForecast', { en: 'Smoke: the next days', es: 'Humo: los próximos días', ca: 'Fum: els propers dies' }, {
      forecast: true,
      prompts: [note('Whether smoke reaches populated areas.', 'Si el humo llega a zonas pobladas.', 'Si el fum arriba a zones poblades.')],
    }),
    fourGlobes(6, { en: 'Decision', es: 'Decisión', ca: 'Decisió' }, {
      prompts: [
        note('Ask for the decision and write it down in the box below before closing.', 'Pide la decisión y escríbela en el cuadro de abajo antes de cerrar.', 'Demana la decisió i escriu-la al quadre de sota abans de tancar.'),
        { kind: DECISION_PROMPT, audience: 'presenter', text: { en: 'What was decided?', es: '¿Qué se ha decidido?', ca: "Què s'ha decidit?" } },
      ],
    }),
  ], now)
}

/** The Decision prompt of a step, if it has one. */
export function decisionPromptOf(step: KhipuStep | undefined): KhipuText | null {
  const p = step?.prompts?.find((x) => x.kind === DECISION_PROMPT)
  return p ? p.text : null
}
